import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useToast } from '../../components/ui/Toast';
import { money } from '../../utils/format';
import { ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { NumberInput } from '../../components/ui/Form';

interface Limit {
  id: string;
  control_code: string;
  role_code: string | null;
  max_value: string;
  unit: 'AMOUNT' | 'PERCENT' | 'COUNT' | 'FINE_GRAM';
  approver_role_code: string | null;
  description: string | null;
  is_active: boolean;
}

const CONTROL_TITLE: Record<string, string> = {
  'cash.payment_amount': 'Cash paid out at once',
  'document.backdated_days': 'Back-dating a document',
  'expense.amount': 'A single expense',
  'rate.override_pct': 'Moving away from the published rate',
  'sales.below_cost': 'Selling below cost',
  'sales.discount_pct': 'Discount on a bill',
  'stock.adjustment_fine_wt': 'Stock written off at once',
};

const UNIT_LABEL: Record<Limit['unit'], string> = {
  AMOUNT: '₹',
  PERCENT: '%',
  COUNT: 'days',
  FINE_GRAM: 'fine g',
};

const show = (l: Limit): string =>
  l.unit === 'AMOUNT' ? money(l.max_value) : `${Number(l.max_value)} ${UNIT_LABEL[l.unit]}`;

/**
 * The limits that decide when a second person has to agree.
 *
 * Every one of these can be raised, lowered, or switched off entirely. They
 * exist because the same shop that wants no interruptions on a Tuesday wants to
 * know, in March, who approved a two-lakh cash payout — but that is the shop's
 * judgement to make, not the software's, and a control nobody can change is
 * just an obstacle.
 *
 * A limit with no role applies to everybody. The owner and the super admin have
 * their own row on each control, set high enough not to interrupt them, so the
 * person who owns the shop is never sent to ask themselves for permission.
 */
export function ControlsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [switching, setSwitching] = useState<Limit | null>(null);
  const [allOff, setAllOff] = useState<boolean | null>(null);

  const limits = useQuery({ queryKey: ['control-limits'], queryFn: () => api.get<{ data: Limit[] }>('/control-limits') });
  const mayEdit = can('workflow.manage');

  const save = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api.patch(`/control-limits/${id}`, patch),
    onSuccess: () => {
      toast('Control updated');
      void client.invalidateQueries({ queryKey: ['control-limits'] });
      setEditing(null);
      setSwitching(null);
    },
    onError: (e) => {
      setError(e);
      setSwitching(null);
    },
  });

  const rows = limits.data?.data ?? [];
  const byControl = [...new Set(rows.map((r) => r.control_code))].sort();
  const anyOn = rows.some((r) => r.is_active);

  const switchAll = useMutation({
    mutationFn: (isActive: boolean) => api.post<{ changed: number }>('/control-limits/all', { isActive }),
    onSuccess: (r, isActive) => {
      toast(
        isActive ? 'Approvals switched back on' : 'Approvals switched off',
        `${r.changed} ${r.changed === 1 ? 'control' : 'controls'} changed`,
      );
      void client.invalidateQueries({ queryKey: ['control-limits'] });
      setAllOff(null);
    },
    onError: (e) => {
      setError(e);
      setAllOff(null);
    },
  });

  return (
    <>
      <PageHeader
        title="Controls"
        subtitle="When a second person has to agree. Raise a limit, lower it, or switch the control off."
        actions={
          mayEdit ? (
            <button className={anyOn ? 'btn-danger' : 'btn-primary'} onClick={() => setAllOff(anyOn)}>
              {anyOn ? 'Switch off all approvals' : 'Switch approvals back on'}
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error ?? limits.error} />

      {!anyOn && rows.length > 0 ? (
        <p className="mb-4 rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-sm">
          Every control is off. Nothing is stopped or sent for approval, whatever the amount. Everything is still
          posted and still recorded — the day book and the audit trail are unchanged.
        </p>
      ) : null}
      {limits.isLoading ? <Loading rows={4} /> : null}

      <div className="space-y-4">
        {byControl.map((code) => {
          const group = rows.filter((r) => r.control_code === code);
          const off = group.every((g) => !g.is_active);
          return (
            <section key={code} className={`overflow-hidden rounded-xl border bg-surface shadow-card ${off ? 'border-dashed border-silver-400' : 'border-silver-100'}`}>
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-silver-100 bg-gold-50/50 px-4 py-2.5">
                <div>
                  <h2 className="text-sm font-semibold">{CONTROL_TITLE[code] ?? code}</h2>
                  <p className="text-2xs text-ink-soft">{group[0]?.description ?? code}</p>
                </div>
                {off ? <span className="text-2xs font-medium text-ink-soft">switched off</span> : null}
              </header>

              <table className="w-full">
                <thead>
                  <tr>
                    <th className="th">Who</th>
                    <th className="th text-right">May go up to</th>
                    <th className="th">Above that, approved by</th>
                    <th className="th">In force</th>
                    <th className="th" />
                  </tr>
                </thead>
                <tbody>
                  {group.map((l) => (
                    <tr key={l.id} className={l.is_active ? '' : 'opacity-60'}>
                      <td className="td">
                        {l.role_code ? (
                          l.role_code.replace(/_/g, ' ').toLowerCase()
                        ) : (
                          <span className="text-ink-soft">everyone else</span>
                        )}
                      </td>
                      <td className="td num text-right">
                        {editing === l.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <NumberInput value={value} onChange={setValue} decimals={l.unit === 'AMOUNT' ? 2 : 4} />
                            <button
                              className="btn-primary py-1 text-2xs"
                              onClick={() => save.mutate({ id: l.id, patch: { maxValue: Number(value || 0).toFixed(2) } })}
                            >
                              Save
                            </button>
                            <button className="btn-quiet py-1 text-2xs" onClick={() => setEditing(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          show(l)
                        )}
                      </td>
                      <td className="td text-2xs">
                        {l.approver_role_code ? l.approver_role_code.replace(/_/g, ' ').toLowerCase() : '—'}
                      </td>
                      <td className="td text-2xs">{l.is_active ? 'yes' : 'no'}</td>
                      <td className="td text-right">
                        {mayEdit && editing !== l.id ? (
                          <>
                            <button
                              className="mr-3 text-2xs text-gold-700 underline"
                              onClick={() => {
                                setEditing(l.id);
                                setValue(String(Number(l.max_value)));
                              }}
                            >
                              Change limit
                            </button>
                            <button className="text-2xs text-ink-soft underline" onClick={() => setSwitching(l)}>
                              {l.is_active ? 'Switch off' : 'Switch on'}
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>

      <ConfirmDialog
        open={allOff !== null}
        tone={allOff ? 'danger' : 'primary'}
        title={allOff ? 'Switch off every approval?' : 'Switch every approval back on?'}
        message={
          allOff
            ? 'Nobody will be stopped again — no discount, write-off, back-dated document or cash payout, at any amount, by anyone. Everything is still posted and still recorded; the software simply stops asking a second person first.'
            : 'Every control returns to the limit it had. Anything already posted stays as it is.'
        }
        detail={
          allOff ? (
            <p>
              One of these is the ₹2,00,000 cash payout limit, which exists because Indian law treats large cash
              dealings strictly. Switching it off is your call — it can be switched back on here at any time.
            </p>
          ) : null
        }
        confirmLabel={allOff ? 'Switch them all off' : 'Switch them on'}
        busy={switchAll.isPending}
        onCancel={() => setAllOff(null)}
        onConfirm={() => switchAll.mutate(!allOff)}
      />

      <ConfirmDialog
        open={!!switching}
        tone={switching?.is_active ? 'danger' : 'primary'}
        title={switching?.is_active ? 'Switch this control off?' : 'Switch this control back on?'}
        message={
          switching?.is_active
            ? 'Nobody will be stopped or asked for approval on it again, whatever the amount. Everything is still recorded and still shows in the day book and the audit trail — it simply goes through without a second person.'
            : 'The limit applies again from now on. Anything already posted stays as it is.'
        }
        detail={
          switching ? (
            <p>
              {CONTROL_TITLE[switching.control_code] ?? switching.control_code} ·{' '}
              {switching.role_code ? switching.role_code.replace(/_/g, ' ').toLowerCase() : 'everyone else'} · currently{' '}
              <span className="num">{show(switching)}</span>
            </p>
          ) : null
        }
        confirmLabel={switching?.is_active ? 'Switch it off' : 'Switch it on'}
        busy={save.isPending}
        onCancel={() => setSwitching(null)}
        onConfirm={() => switching && save.mutate({ id: switching.id, patch: { isActive: !switching.is_active } })}
      />
    </>
  );
}
